import { useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  DialogActions,
  Button,
  Alert,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  findCategoryWithCode,
  addCategoryAndSubCategory,
  updateMultiNumericCategory,
} from '../../../services/CategoryHelper';
import {
  showAddCategoryDialog,
  getCategoryList,
  addCategory,
  updateView,
  updateCategoryModify,
} from '../../../store/states/CategorySlice';
import { AppDispatch, AppState } from '../../../store/AppStore';
import { useTranslation } from 'react-i18next';
import AutocompleteInput from '../../InputComponents/AutocompleteInput';
import { EActionType, ECategoryType, EPrimitiveType, ERecordLength } from '../../../data/Constants';
import DraggableComponent from '../../DraggableComponent';
import { CategoryApi } from '../../../api/CategoryApi';
import AutoCompleteSearchComponent from '../../../components/InputComponents/AutoCompleteSearchComponent';

import type { ICategory, IView, IOption, ISection } from '../../../../types';

const AddCategoryComponent = ({ open }: { open: boolean }) => {
  const { t } = useTranslation();
  const [category, setCategory] = useState('');
  const [error, setError] = useState('');
  const [selectedValue, setSelectedValue] = useState<IOption | null>(null);
  const [currentCategoryType, setCurrentCategoryType] = useState<IOption | null>(null);
  const [validationError, setValidationError] = useState<string>('');
  const [searchLimitWarning, setSearchLimitWarning] = useState<string>('');
  const [multiNumericOptions, setMultiNumericOptions] = useState<IOption[]>([]);
  const [showMultiNumericOptions, setShowMultiNumericOptions] = useState(false);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [initialValue, setInitialValue] = useState<string | null>('');

  const dispatch = useDispatch<AppDispatch>();
  const product = useSelector((state: AppState) => state.procos.product);
  const viewDetails: IView = useSelector((state: AppState) => state.category.view);
  const selectedSection: ISection = useSelector(
    (state: AppState) => state.category.sectionForAddSubSection
  );
  const categoriesList: Array<ICategory> = useSelector(
    (state: AppState) => state.category.categories
  );

  //Search Value min and max length values
  const minLength = 3;
  const maxLength = 40;

  //category type dropdown  options
  const CategoryTypes = [
    { code: ECategoryType.Standard, name: `${t('labels.standard')}` },
    { code: ECategoryType.MultiNumeric, name: `${t('labels.multiNumeric')}` },
  ];

  useEffect(() => {
    //Dispatch the getCategoryList action to fetch the general category list from the API
    if (categoriesList?.length === 0) {
      dispatch(getCategoryList({ page: 1, limit: ERecordLength.RL500 }));
    }

    // Show the current multinumeric vale to the search box on the dialog when click on edit icon
    if (selectedSection?.action === EActionType.Edit) {
      setCurrentCategoryType(CategoryTypes[1]);
      setSelectedValue({ code: selectedSection.code, description: selectedSection.description });
      setCategory(selectedSection.description);
      setInitialValue(selectedSection.description);
    }

    // for second level show multi numeric as default category type
    //General category is not applicable from 2nd level category
    if (selectedSection?.hideGeneral) {
      setCurrentCategoryType(CategoryTypes[1]);
    }
  }, []);

  //This method triggers when close the add category dialo window to reset the local state values
  const handleClose = () => {
    setCategory('');
    setCurrentCategoryType(null);
    setSelectedValue(null);
    setError('');
    setSearchLimitWarning('');
    setValidationError('');
    setShowMultiNumericOptions(false);
    dispatch(showAddCategoryDialog({ section: null, flag: false }));
  };

  // This method called when click on add icon button or add button to add the new category on root level or sub section level
  const handleAddCategory = () => {
    if (!category) {
      const message = t('messages.category.selectCategory');
      setError(message);
      return;
    }

    let newCategoryInfo = null;

    //Create the category object for selected category to store the state for General and multi-Numeric
    if (!selectedSection || currentCategoryType?.code === ECategoryType.Standard) {
      const selectedCategory: ICategory = findCategoryWithCode(categoriesList, category);

      //Create the category Id using product id and sequence number
      //ex: productId:722223, sequence:90 ---> S722223_90
      const subSectionsId: string = `S${product.id}_${selectedCategory.sequence}`;
      newCategoryInfo = {
        code: subSectionsId,
        description: selectedCategory.name,
        type: ECategoryType.Standard.toString(),
      };
    } else {
      //Split the parent category id by '_' to get the sequence number (ex: S722223_90- > [S722223, 90])
      const sequence = selectedSection.code?.split('_')[1];

      //Create the category Id using product id,multi-Numeric code and  sequence number
      //ex: productId:722223, multi-Numeric code:FNA2780, sequence:90 ---> S722223_FNA2780_90)
      const subSectionsId: string = `S${product.id}_${category}_${sequence}`;

      newCategoryInfo = {
        code: subSectionsId,
        description: selectedValue?.name,
        type: ECategoryType.MultiNumeric.toString(),
      };
    }

    //Get the current selected category and its subsections
    const categoryWithSubCategory: Array<ISection> | string = addCategoryAndSubCategory(
      viewDetails,
      selectedSection,
      newCategoryInfo
    );

    if (typeof categoryWithSubCategory === EPrimitiveType.String) {
      setError(t(categoryWithSubCategory as string));
    } else {
      //Diapatch the Action to update the slice with the selected category to re-render the component
      dispatch(addCategory(categoryWithSubCategory));
      handleClose();
    }

    setSearchLimitWarning('');
  };

  // On selection of category from dropdown, it's set the selcted category code.
  const handleChange = (selectedCategory: IOption) => {
    setSelectedValue(selectedCategory);
    setError('');
    setSearchLimitWarning('');
    setValidationError('');
    setShowMultiNumericOptions(false);
    setCategory(selectedCategory?.code);
  };

  /**
   * This method trigger when user change the category type in the dropdown
   * This method will set the current category types to the state
   * This method call the api to get the category list to show in the dropdown to add category
   * @param {ICategoryType} value input selectchangeevent params
   * @returns {void}
   */
  const handleCategoryTypeChange = (value: IOption): void => {
    setCurrentCategoryType(value);

    //remove the current category values
    setSelectedValue(null);
    setCategory('');

    //remove the error
    setError('');
  };

  /**
   * This function calls the api to fetch the multi numeric features by the search key
   * @param {string} searchValue the input search value
   * @returns {void}
   */
  const handleSearchMultiNumeric = async (searchValue: string): Promise<void> => {
    if (searchValue && searchValue?.length >= minLength && searchValue?.length <= maxLength) {
      setSearchLoading(true);
      const response = await CategoryApi.getMultiNumericFeatures({ searchKey: searchValue }, true);
      setSearchLoading(false);
      //After fetching the api response, set the values to the searchOptions state to load the features to select it.
      if (response && !response?.error) {
        const pageInfo = JSON.parse(response?.headers['x-pagination']);
        setMultiNumericOptions(response.data);
        setShowMultiNumericOptions(true);

        //show the error message if total count is greater than the limit ( limit:50 )
        //because in search options restrict to show more than 50 records
        if (pageInfo.total > pageInfo.limit) {
          setSearchLimitWarning(
            t('messages.category.searchLimitWarning', {
              total: pageInfo.total,
              limit: pageInfo.limit,
            })
          );
        } else {
          setSearchLimitWarning('');
        }
      } else {
        //Show the error message if api returns error.
        setValidationError(t('messages.category.multiNumericReadError'));
        setShowMultiNumericOptions(false);
      }
    }
  };

  /**
   * This function validate new search value and show the error message if not valid
   * @param {string} value  serch value
   * @returns {void}
   */
  const validate = (value: string) => {
    //Show the warning message if search value length is less than 4 or greater than 40
    if (value.length < minLength) {
      setValidationError(
        t('messages.category.multiNumericMinValidationError', { minLength: minLength })
      );
      return;
    } else if (value.length > maxLength) {
      setValidationError(
        t('messages.category.multiNumericMaxValidationError', { maxLength: maxLength })
      );
      return;
    }

    //remove the error message after enter the  valid search value
    setValidationError('');
  };

  /**
   * This function update the existing multinumeric category in the master view data
   */
  const handleSaveCategoryAfterEdit = () => {
    //get the old multi numeric code from id
    //ex: S722223_FNA2780_990 --> FNA2780
    const existingId = selectedSection.code.split('_')[1];

    //replacing old id to the new multi numeric id
    //ex: S722223_FNA2780_990 --> S722223_FNA3280_990
    const newId = selectedSection.code.replace(existingId, selectedValue?.code);

    //update the new multi numeric category to the master view
    const newSelectedValue = { ...selectedValue, code: newId };
    const masterData = structuredClone(viewDetails);
    updateMultiNumericCategory(masterData, selectedSection.code, newSelectedValue);
    dispatch(updateView(masterData));
    dispatch(updateCategoryModify());
    handleClose();
  };

  return (
    <Dialog
      maxWidth={'xs'}
      open={open}
      onClose={handleClose}
      aria-labelledby="draggable-dialog-title"
      PaperComponent={DraggableComponent}
      className="common-dialog-style alert-dialog dialog-content add-category-dialog"
    >
      <DialogTitle className=" text-left cursor-move">
        {selectedSection == null
          ? `${t('labels.addCategory')} ( ${product.id} )`
          : `${selectedSection?.description} ( ${product.id} )`}
      </DialogTitle>
      <DialogContent>
        <Box
          noValidate
          component="form"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            m: 'auto',
            width: 'fit-content',
          }}
        >
          {selectedSection && (
            <FormControl sx={{ mt: 2, minWidth: 120 }}>
              <AutocompleteInput
                data={CategoryTypes}
                id="category-types-select"
                handleChange={handleCategoryTypeChange}
                label={t('labels.selectCategoryType')}
                disabled={selectedSection?.hideGeneral ?? false}
                value={currentCategoryType}
                disableClear={true}
              />
            </FormControl>
          )}

          {selectedSection && currentCategoryType?.code === ECategoryType.MultiNumeric && (
            <AutoCompleteSearchComponent
              label={t('labels.searchMultiNumericCategory')}
              initialValue={initialValue}
              maxLength={maxLength}
              showOptions={showMultiNumericOptions}
              options={multiNumericOptions}
              error={validationError}
              warning={searchLimitWarning}
              loading={searchLoading}
              validate={validate}
              handleSearch={handleSearchMultiNumeric}
              setShowOptions={setShowMultiNumericOptions}
              onValueSelect={handleChange}
            />
          )}

          {(!selectedSection || currentCategoryType?.code === ECategoryType.Standard) && (
            <FormControl sx={{ mt: 2, minWidth: 120 }}>
              <AutocompleteInput
                data={
                  categoriesList !== undefined && categoriesList?.length > 0 ? categoriesList : []
                }
                id="select-category-general"
                handleChange={handleChange}
                label={t('button.selectCategory')}
                disabled={false}
                value={selectedValue}
                disableClear={false}
                showSequenceCode={true}
              />
            </FormControl>
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          data-testid="add-category-close-button"
          className="text-capitalize"
          variant="outlined"
          onClick={handleClose}
        >
          {t('button.close')}
        </Button>
        <Button
          data-testid="add-category-add-button"
          className="text-capitalize"
          variant="contained"
          disabled={!selectedValue}
          onClick={
            selectedSection?.type === ECategoryType.MultiNumeric
              ? handleSaveCategoryAfterEdit
              : handleAddCategory
          }
        >
          {selectedSection?.type === ECategoryType.MultiNumeric ? 'Save' : t('button.add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCategoryComponent;
